import React, { use, useCallback, useMemo, useState } from "react";
import { Linking, Platform, Pressable, ScrollView, StyleSheet } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client/react";
import { UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { AuthManagerContext } from "../../contexts/AuthManager";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar, useSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import * as DocumentPicker from "expo-document-picker";
import { v4 as uuidv4 } from "uuid";
import {
    CurriculumVitae,
    CurriculumVitaeInput,
    CvSource,
    DocumentType,
    EducationLevel,
} from "../../__generated__/graphql";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { XOR } from "ts-essentials";
import { BodyMediumText, TitleMediumText } from "../../components/FormattedText";
import OptionsField from "../../components/OptionsField";
import { useTheme } from "../../hooks/use-theme";
import TextField from "../../components/TextField";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";
import { FIELD_HEIGHT } from "../../components/BaseField";
import SelectField from "../../components/SelectField";
import ListItem from "../../ui_atoms/ListItem";
import { SelectChooser, SelectItemType } from "../../components/select/SelectChooser";
import { useUploadDocument } from "../../hooks/documents";
import Gap from "../../ui_atoms/Gap";
import QuantityField from "../../components/QuantityField";
import { sanitizeUrl } from "../../utils";
import DocumentEditorModal from "../../components/document/DocumentEditorModal";
import { useSetAlert } from "../../hooks/use-set-alert";

type FormData = XOR<
    { hasExperiences?: false },
    {
        hasExperiences: true;
        curriculumVitae: CurriculumVitae | null;
        pdfFileName?: string;
    }
>;

type ExperiencesEditorScreenProps = StackScreenProps<RootStackParamList, "ExperiencesEdit">;

const ExperiencesEditorScreen = ({ navigation, route }: ExperiencesEditorScreenProps) => {
    const { profileId } = (route.params || {}) as RootStackParamList["ExperiencesEditModal"];
    const styles = useStyles();
    const { showSnackbar } = useSnackbar();
    const { setAlert, setPrompt } = useSetAlert();
    const { marginSize } = useTheme();
    const { user, refreshUser } = use(AuthManagerContext);
    const [isImportVisible, setIsImportVisible] = useState(false);
    const [isPdfDocumentVisible, setIsPdfDocumentVisible] = useState(false);
    const [updateWorkerProfile, { error: saveError, loading: saving, reset: resetUpdate }] =
        useMutation(
            UPDATE_WORKER_PROFILE,
            profileId ? { context: { liftProfile: profileId } } : undefined,
        );

    useApolloErrorSnackbar(saveError, resetUpdate);

    const i18n = useI18n();
    const { colors } = useTheme();

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            ...(user?.workerProfile?.curriculumVitae?.source
                ? user.workerProfile.curriculumVitae.source !== CvSource.Empty
                    ? {
                          hasExperiences: true,
                          curriculumVitae: user?.workerProfile?.curriculumVitae,
                      }
                    : { hasExperiences: false }
                : {}),
        },
    });

    const cvSource = watch("curriculumVitae.source") || CvSource.Empty;
    const pdfFileName = watch("pdfFileName");
    const linkedinLink = watch("curriculumVitae.link");
    const pdfDocumentUrl = watch("curriculumVitae.pdfDocumentUrl");
    const hasExperiences = watch("hasExperiences");

    const { currentDocumentName, viewCvLabel, onViewCvPress } = useMemo(() => {
        switch (cvSource) {
            case CvSource.Internal:
                return {
                    currentDocumentName: i18n.t("screens.profile_editor.experiences.create_cv"),
                };
            case CvSource.LinkedinLink:
                return {
                    currentDocumentName: linkedinLink || "",
                    viewCvLabel: i18n.t("screens.profile_editor.experiences.view_link"),
                    onViewCvPress: () => {
                        if (linkedinLink) {
                            const sanitizedUrl = sanitizeUrl(linkedinLink);
                            if (sanitizedUrl) {
                                void Linking.openURL(sanitizedUrl);
                            }
                        }
                    },
                };
            case CvSource.PdfDocument:
                return {
                    currentDocumentName:
                        pdfFileName || i18n.t("screens.profile_editor.experiences.pdf_cv"),
                    viewCvLabel: i18n.t("screens.profile_editor.experiences.view_document"),
                    onViewCvPress: () => {
                        setIsPdfDocumentVisible(true);
                    },
                };
            default:
                return { currentDocumentName: "" };
        }
    }, [cvSource, i18n, linkedinLink, pdfFileName]);

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                await updateWorkerProfile({
                    variables: {
                        changes: {
                            curriculumVitae: (data.hasExperiences && data.curriculumVitae?.source
                                ? {
                                      source: data.curriculumVitae.source,
                                      ...(data.curriculumVitae?.source === CvSource.PdfDocument
                                          ? {
                                                pdfDocumentUrl: data.curriculumVitae.pdfDocumentUrl,
                                            }
                                          : {}),
                                      ...(data.curriculumVitae?.source === CvSource.LinkedinLink
                                          ? {
                                                link: data.curriculumVitae.link,
                                            }
                                          : {}),
                                      ...(data.curriculumVitae?.source === CvSource.Internal
                                          ? {
                                                trainings: data.curriculumVitae.trainings,
                                                experiences: data.curriculumVitae.experiences?.map(
                                                    (v) => ({
                                                        professionName: v.professionName,
                                                        yearsOfExperience:
                                                            v.yearsOfExperienceDecimal,
                                                    }),
                                                ),
                                            }
                                          : {}),
                                  }
                                : {
                                      source: CvSource.Empty,
                                  }) as CurriculumVitaeInput,
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.();
            } finally {
                // this is handled by updateError in useMutation
            }
        },
        [navigation, refreshUser, updateWorkerProfile],
    );

    const [isUploadingCv, setIsUploadingCv] = useState(false);

    const { uploadDocument } = useUploadDocument();

    const onPickPdfCv = useCallback(() => {
        void (async () => {
            setIsUploadingCv(true);
            const pickerResult = await DocumentPicker.getDocumentAsync({
                type: ["application/pdf", "images/*"],
            });
            const pdfCvAsset = pickerResult.assets?.[0];
            if (pdfCvAsset) {
                const newDocumentId = uuidv4();
                const uploadedDocumentUrl = await uploadDocument(
                    pdfCvAsset,
                    DocumentType.CurriculumVitae,
                    true,
                    newDocumentId,
                );
                setValue("pdfFileName", pdfCvAsset.name);
                setValue("curriculumVitae.source", CvSource.PdfDocument);
                setValue("curriculumVitae.pdfDocumentUrl", uploadedDocumentUrl, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }
            setIsUploadingCv(false);
        })();
    }, [setValue, uploadDocument]);

    const onPickLinkedin = useCallback(() => {
        setPrompt({
            title: i18n.t("screens.profile_editor.experiences.paste_linkedin_url"),
            callbackOrButtons: (text) => {
                if (
                    text.match(
                        /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/in\/[a-zA-Z0-9-_%]+\/?$/,
                    )
                ) {
                    setValue("curriculumVitae.source", CvSource.LinkedinLink);
                    setValue("curriculumVitae.link", sanitizeUrl(text), {
                        shouldValidate: true,
                        shouldDirty: true,
                    });
                } else {
                    showSnackbar({
                        mode: "error",
                        text: i18n.t("screens.profile_editor.experiences.invalid_linkedin_link"),
                    });
                }
            },
            keyboardType: "url",
        });
    }, [i18n, setPrompt, setValue, showSnackbar]);

    const confirmDeleteCv = useCallback(() => {
        const baseKey = `screens.profile_editor.experiences.clear.${cvSource === CvSource.LinkedinLink ? cvSource : "other"}`;
        setAlert({
            title: i18n.t(`${baseKey}.title`),
            message: i18n.t(`${baseKey}.description`),
            buttons: [
                {
                    text: i18n.t("delete"),
                    onPress: () => {
                        reset({
                            hasExperiences: true,
                            curriculumVitae: {
                                experiences: [],
                                trainings: [],
                                source: CvSource.Empty,
                                link: undefined,
                                pdfDocumentUrl: undefined,
                            },
                        });
                    },
                    style: "destructive",
                },
                { text: i18n.t("cancel"), onPress: () => {}, style: "cancel", isPreferred: true },
            ],
        });
    }, [cvSource, i18n, reset, setAlert]);

    useCloseEditor(isDirty && !saving);

    const { chainInputFocus } = useChainInputFocus();

    const {
        append: appendTraining,
        remove: removeTrainingAtIndex,
        fields: trainingFields,
    } = useFieldArray({
        name: "curriculumVitae.trainings",
        control,
    });

    const {
        append: appendExperience,
        remove: removeExperienceAtIndex,
        fields: experienceFields,
    } = useFieldArray({
        name: "curriculumVitae.experiences",
        control,
    });

    const educationLevels = useMemo<{ value: EducationLevel; label: string }[]>(
        () =>
            (Object.values(EducationLevel) as EducationLevel[])
                .filter((educationLevel) => educationLevel !== EducationLevel.Without)
                .map((key) => ({
                    value: key,
                    label: i18n.t(`education_level.${key}`),
                })),
        [i18n],
    );

    const isInternalCv = cvSource === CvSource.Internal;

    const searchCvSourceItems = useMemo<SelectItemType<CvSource>[]>(
        () => [
            {
                label: "",
                labels: [
                    {
                        text: i18n.t("screens.profile_editor.experiences.import_pdf"),
                        mode: "cta",
                    },
                ],
                value: CvSource.PdfDocument,
                rightActionMode: "none",
            },
            {
                label: "",
                labels: [
                    {
                        text: i18n.t("screens.profile_editor.experiences.import_linkedin"),
                        mode: "cta",
                    },
                ],
                value: CvSource.LinkedinLink,
                rightActionMode: "none",
            },
        ],
        [i18n],
    );

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <ScrollView>
                <VStack v={1} gap={0.5} alignment="stretch" h="2/3">
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.experiences.has_education")}
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="hasExperiences"
                            rules={{
                                validate: (v) => {
                                    return v !== undefined;
                                },
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                    <Controller
                        control={control}
                        name="curriculumVitae.source"
                        rules={{
                            required: hasExperiences,
                            validate: (v) => {
                                return (
                                    !hasExperiences ||
                                    (v !== undefined &&
                                        (!isInternalCv ||
                                            (isInternalCv &&
                                                (experienceFields.length > 0 ||
                                                    trainingFields.length > 0))))
                                );
                            },
                        }}
                        render={({ field: { onChange } }) =>
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            hasExperiences ? (
                                <>
                                    <BodyMediumText>
                                        {i18n.t("screens.profile_editor.experiences.fill_in_data")}
                                    </BodyMediumText>
                                    {cvSource === CvSource.Empty ? (
                                        <>
                                            <SelectChooser
                                                visible={isImportVisible}
                                                selectable={false}
                                                onDismiss={() => {
                                                    setIsImportVisible(false);
                                                }}
                                                isLegacyLabel={false}
                                                searchItems={searchCvSourceItems}
                                                onSelect={(item) => {
                                                    setIsImportVisible(false);
                                                    switch (item.value) {
                                                        case CvSource.PdfDocument:
                                                            setTimeout(
                                                                onPickPdfCv,
                                                                // iOS have an issue where opening the native document picker directly from a modal doesn't work (it closes the picker modal directly after opening)
                                                                // See this issue :
                                                                // https://github.com/react-native-documents/document-picker/issues/661
                                                                // An acceptable workaround on iOS is to set a small delay before calling onPickPdfCv()
                                                                Platform.OS === "ios" ? 400 : 0,
                                                            );
                                                            break;
                                                        case CvSource.LinkedinLink:
                                                            onPickLinkedin();
                                                            break;
                                                        default:
                                                            break;
                                                    }
                                                }}
                                            />
                                            <ListItem
                                                loading={isUploadingCv}
                                                onPress={() => setIsImportVisible(true)}
                                                labels={[
                                                    {
                                                        text: i18n.t(
                                                            "screens.profile_editor.experiences.import_cv",
                                                        ),
                                                        mode: "cta",
                                                    },
                                                ]}
                                                style={{ marginHorizontal: -marginSize }}
                                                rightActionMode="medium"
                                                rightIconColor={colors.secondary}
                                                rightIcon="cloud-upload"
                                            />
                                            <ListItem
                                                loading={isUploadingCv}
                                                onPress={() => onChange(CvSource.Internal)}
                                                labels={[
                                                    {
                                                        text: i18n.t(
                                                            "screens.profile_editor.experiences.create_cv",
                                                        ),
                                                        mode: "cta",
                                                    },
                                                ]}
                                                style={{ marginHorizontal: -marginSize }}
                                                rightActionMode="medium"
                                                rightIconColor={colors.secondary}
                                                rightIcon="add"
                                            />
                                        </>
                                    ) : [CvSource.LinkedinLink, CvSource.PdfDocument].includes(
                                          cvSource,
                                      ) ? (
                                        <>
                                            <ListItem
                                                onPress={confirmDeleteCv}
                                                description={currentDocumentName}
                                                style={{ marginHorizontal: -marginSize }}
                                                rightActionMode="medium"
                                                rightIconColor={colors.error}
                                                rightIcon="trash"
                                            />
                                            <ListItem
                                                onPress={onViewCvPress}
                                                labels={[{ text: viewCvLabel, mode: "cta" }]}
                                                style={{ marginHorizontal: -marginSize }}
                                                rightActionMode="none"
                                            />
                                            <DocumentEditorModal
                                                visible={isPdfDocumentVisible}
                                                document={{
                                                    id: "",
                                                    type: DocumentType.CurriculumVitae,
                                                    title: i18n.t(
                                                        "screens.profile_editor.experiences.my_cv",
                                                    ),
                                                    documentUrl: pdfDocumentUrl ?? null,
                                                }}
                                                onClose={() => {
                                                    setIsPdfDocumentVisible(false);
                                                }}
                                            />
                                        </>
                                    ) : null}
                                </>
                            ) : null
                        }
                    />
                    {hasExperiences && isInternalCv && (
                        <>
                            <TitleMediumText>
                                {i18n.t("screens.profile_editor.experiences.trainings")}
                            </TitleMediumText>
                            <VStack gap="2/3">
                                {trainingFields.length > 0 && <></>}
                                {trainingFields?.map(({ id }, index) => {
                                    return (
                                        <React.Fragment key={id}>
                                            <Controller
                                                control={control}
                                                name={`curriculumVitae.trainings.${index}.educationLevel`}
                                                rules={{
                                                    required: !!trainingFields.find(
                                                        (v) => v.id === id,
                                                    ),
                                                }}
                                                render={({
                                                    field: { onChange, onBlur, value },
                                                    fieldState: { error },
                                                }) => (
                                                    <SelectField
                                                        isSelfContained
                                                        style={{
                                                            borderBottomRightRadius: 0,
                                                            borderBottomLeftRadius: 0,
                                                        }}
                                                        error={error?.message}
                                                        value={value || undefined}
                                                        label={i18n.t(
                                                            "screens.profile_editor.experiences.education_level",
                                                        )}
                                                        data={educationLevels}
                                                        onSelect={(e) => {
                                                            onChange(e.value);
                                                        }}
                                                        onBlur={onBlur}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name={`curriculumVitae.trainings.${index}.trainingName`}
                                                rules={{
                                                    required: !!trainingFields.find(
                                                        (v) => v.id === id,
                                                    ),
                                                }}
                                                render={({
                                                    field: { onChange, onBlur, value, name },
                                                    fieldState: { error },
                                                }) => (
                                                    <TextField
                                                        {...chainInputFocus(name)}
                                                        isSelfContained
                                                        style={styles.stackedField}
                                                        error={error?.message}
                                                        value={value || ""}
                                                        label={i18n.t(
                                                            "screens.profile_editor.experiences.training_name",
                                                        )}
                                                        onChangeText={onChange}
                                                        onBlur={onBlur}
                                                    />
                                                )}
                                            />
                                            <Pressable
                                                style={styles.removeSubButton}
                                                onPress={() => removeTrainingAtIndex(index)}
                                            >
                                                <BodyMediumText color={colors.error}>
                                                    {i18n.t("delete")}
                                                </BodyMediumText>
                                            </Pressable>
                                        </React.Fragment>
                                    );
                                })}
                                <ListItem
                                    // Add a empty element
                                    //
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    onPress={() => appendTraining()}
                                    labels={[
                                        {
                                            text: i18n.t(
                                                "screens.profile_editor.experiences.add_training",
                                            ),
                                            mode: "cta",
                                        },
                                    ]}
                                    style={{ marginHorizontal: -marginSize }}
                                    rightActionMode="medium"
                                    rightIconColor={colors.secondary}
                                    rightIcon="add"
                                />
                            </VStack>
                            <Gap gap={1} />
                            <TitleMediumText>
                                {i18n.t("screens.profile_editor.experiences.experiences")}
                            </TitleMediumText>
                            <VStack gap="2/3">
                                {experienceFields.length > 0 && <></>}
                                {experienceFields?.map(({ id }, index) => {
                                    return (
                                        <React.Fragment key={id}>
                                            <Controller
                                                control={control}
                                                name={`curriculumVitae.experiences.${index}.professionName`}
                                                rules={{
                                                    required: !!experienceFields.find(
                                                        (v) => v.id === id,
                                                    ),
                                                }}
                                                render={({
                                                    field: { onChange, onBlur, value, name },
                                                    fieldState: { error },
                                                }) => (
                                                    <TextField
                                                        {...chainInputFocus(name)}
                                                        isSelfContained
                                                        style={{
                                                            borderBottomRightRadius: 0,
                                                            borderBottomLeftRadius: 0,
                                                        }}
                                                        error={error?.message}
                                                        value={value || ""}
                                                        label={i18n.t(
                                                            "screens.profile_editor.experiences.profession_name",
                                                        )}
                                                        onChangeText={onChange}
                                                        onBlur={onBlur}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name={`curriculumVitae.experiences.${index}.yearsOfExperienceDecimal`}
                                                rules={{
                                                    required: !!experienceFields.find(
                                                        (v) => v.id === id,
                                                    ),
                                                    validate: (v) => !!v, // Avoid setting 0 years of experiences
                                                }}
                                                render={({
                                                    field: { onChange, onBlur, value, name },
                                                    fieldState: { error },
                                                }) => (
                                                    <QuantityField
                                                        {...chainInputFocus(name)}
                                                        isSelfContained
                                                        decimalPlaces={1}
                                                        maxValue={99.9}
                                                        style={styles.stackedField}
                                                        error={error?.message}
                                                        keyboardType="number-pad"
                                                        textAlign="left"
                                                        value={
                                                            value ? parseFloat(value) : undefined
                                                        }
                                                        label={i18n.t(
                                                            "screens.profile_editor.experiences.years_of_experience",
                                                        )}
                                                        onChange={onChange}
                                                        onBlur={onBlur}
                                                    />
                                                )}
                                            />
                                            <Pressable
                                                style={styles.removeSubButton}
                                                onPress={() => removeExperienceAtIndex(index)}
                                            >
                                                <BodyMediumText color={colors.error}>
                                                    {i18n.t("delete")}
                                                </BodyMediumText>
                                            </Pressable>
                                        </React.Fragment>
                                    );
                                })}
                                <ListItem
                                    // Add a empty element
                                    //
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    onPress={() => appendExperience()}
                                    labels={[
                                        {
                                            text: i18n.t(
                                                "screens.profile_editor.experiences.add_experience",
                                            ),
                                            mode: "cta",
                                        },
                                    ]}
                                    style={{ marginHorizontal: -marginSize }}
                                    rightActionMode="medium"
                                    rightIconColor={colors.secondary}
                                    rightIcon="add"
                                />
                            </VStack>
                            <ListItem
                                onPress={confirmDeleteCv}
                                labels={[
                                    {
                                        text: i18n.t(
                                            "screens.profile_editor.experiences.clear_data",
                                        ),
                                        mode: "redCta",
                                    },
                                ]}
                                style={{ marginHorizontal: -marginSize }}
                                rightActionMode="medium"
                                rightIconColor={colors.notification}
                                rightIcon="trash"
                            />
                        </>
                    )}
                </VStack>
            </ScrollView>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { colors, roundness } = useTheme();

    return StyleSheet.create({
        removeSubButton: {
            borderWidth: 2,
            borderColor: colors.onPrimary,
            height: FIELD_HEIGHT,
            borderTopWidth: 0,
            borderBottomRightRadius: roundness,
            borderBottomLeftRadius: roundness,
            alignItems: "center",
            justifyContent: "center",
        },
        stackedField: {
            borderTopRightRadius: 0,
            borderTopLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            marginTop: -2,
            alignSelf: "auto",
        },
    });
};

export default ExperiencesEditorScreen;
